import React, { ReactNode, useEffect, useMemo } from 'react'
import { Button, Card, Col, Form, FormInstance, Row, Spin } from 'antd'

import type { EAReportContextValue } from './Context'
import EAReportContext from './Context'
import EAReportDateTimeFilter from './Filters/DateTime'
import adjustDateRange, { DateRangeFilters } from './Filters/DateTime/utils/adjustDateRange'

const { useForm } = Form

export interface EAReportProps<TFilter = Record<string, unknown>> {
  dataRenderer?: ReactNode
  disableDate?: boolean
  disableDateWhenField?: string
  disableTime?: boolean
  exportRenderer?: ReactNode
  renderFilter?: (form: FormInstance) => ReactNode
  filterRenderer?: (form: FormInstance) => ReactNode
  loading?: boolean
  title: string
  onSubmit: (filtros: TFilter) => void
  enableInfo?: boolean
  infoRenderer?: (form: FormInstance) => ReactNode
}

const EAReport = <TFilter = Record<string, unknown>,>({
  dataRenderer = null,
  disableDate = false,
  disableDateWhenField,
  disableTime = false,
  exportRenderer = null,
  renderFilter,
  filterRenderer,
  loading = false,
  title,
  onSubmit,
  enableInfo = false,
  infoRenderer,
}: EAReportProps<TFilter>) => {
  const [form] = useForm()
  const watchedDisableDateField = Form.useWatch<string | undefined>(disableDateWhenField, form)
  const disableDateByField = typeof watchedDisableDateField === 'string' && watchedDisableDateField.trim().length > 0
  const contextValue = useMemo<EAReportContextValue>(() => ({ title }), [title])
  const activeFilterRenderer = renderFilter ?? filterRenderer

  useEffect(() => {
    if (!disableDateByField) return

    form.setFields([
      { name: 'startDate', errors: [] },
      { name: 'endDate', errors: [] },
      { name: 'startHour', errors: [] },
      { name: 'endHour', errors: [] },
    ])
  }, [disableDateByField, form])

  return (
    <Spin spinning={loading}>
      <EAReportContext.Provider value={contextValue}>
        <Form
          form={form}
          layout="vertical"
          style={{ marginBottom: 16 }}
          validateMessages={{
            required: 'Campo obrigatório.',
          }}
          onFinish={(filtros: DateRangeFilters) => {
            const result =
              disableDate || disableDateByField
                ? (filtros as unknown as TFilter)
                : (adjustDateRange(filtros) as unknown as TFilter)
            onSubmit(result)
          }}
        >
          <Row gutter={16}>
            <Col span={enableInfo ? 16 : 24}>
              <Card
                actions={[
                  <Button htmlType="submit" key="submit">
                    Filtrar
                  </Button>,
                ]}
                title={title}
              >
                {activeFilterRenderer?.(form)}

                {disableDate ? null : (
                  <EAReportDateTimeFilter disabled={disableDateByField} disableTime={disableTime} />
                )}
              </Card>
            </Col>
            {enableInfo && infoRenderer && (
              <Col span={8}>
                <Card title="Resumo">{infoRenderer(form)}</Card>
              </Col>
            )}
          </Row>
        </Form>

        {exportRenderer}

        {dataRenderer}
      </EAReportContext.Provider>
    </Spin>
  )
}

export default EAReport
